// Plan 08-03 — ThemeDescriptor type + the single canonical theme key + locked
// CSS-var key list.
//
// Schema source: 08-UI-SPEC.md §"Theme Descriptor Schema". Universal action
// tokens (Sun Yellow / Lime / Crimson / Hot Pink / Bone / Inkwell / cube-stroke
// / coin) are NOT part of this descriptor — they live at `:root` in globals.css
// (DEC-001 / DEC-013 / DEC-016).
//
// WS0b (ARCA-87) THEME COLLAPSE: the board directive was "don't keep theming".
// The 4-theme architecture is gone — there is one static take-two palette
// (`court-of-runes`) and no runtime theme swap. `THEME_KEYS` retains a single
// entry so the descriptor / atlas / manifest pipeline (which still ships the
// canonical theme's atlas + audio + rune tokens) keeps one source of truth.
// `CSS_VAR_KEYS` is the 13-key parity contract the canonical manifest's
// `css_vars` block must match.

export const THEME_KEYS = ['court-of-runes'] as const

export type ThemeKey = (typeof THEME_KEYS)[number]

export const DEFAULT_THEME: ThemeKey = 'court-of-runes'

/**
 * The 13 locked theme-identity CSS-var keys (UI-SPEC §"Theme palettes").
 * The canonical manifest's `css_vars` block MUST carry exactly these keys (no
 * extras, no missing). Atlas-parity test asserts this. The two extended cube
 * tokens (`--color-cube-face`, `--color-cube-side`) are declared statically at
 * `:root` in theme.css and consumed by pieces.css recipes (`.lcube`, etc.) —
 * they are not part of this manifest-parity contract.
 */
export const CSS_VAR_KEYS = [
  '--color-bg-deep',
  '--color-bg-mid',
  '--color-bg-highlight',
  '--color-surface-deep',
  '--color-surface-mid',
  '--color-surface-light',
  '--color-surface-stroke',
  '--color-magic',
  '--color-magic-deep',
  '--color-player-a',
  '--color-player-a-glow',
  '--color-player-b',
  '--color-player-b-glow',
] as const

export type CssVarKey = (typeof CSS_VAR_KEYS)[number]

/**
 * Plan 30-01 — Runespring `--rune-*` token namespace.
 *
 * Phase 30 extends each launch theme's manifest with 11 new `--rune-*` keys
 * (UI-SPEC §3 "Theme Token Manifest"). 10 are hex colors mirrored under
 * pixi_uniforms as [r,g,b] floats; the 11th (--rune-affinity-halo-opacity)
 * is a scalar float and is excluded from pixi_uniforms. Token presence is
 * enforced by rune-tokens.test.ts; downstream LetterCubeSprite (Plan 30-02)
 * reads these via the theme descriptor.
 *
 * The `--color-*` keys above are theme-identity tokens (chrome / surface /
 * magic / player). The `--rune-*` keys are board-cell tokens (tier overlays /
 * anchor sigil / hollow crack / affinity halo). They form an additive
 * namespace — existing manifests are extended, not replaced.
 */
export const RUNE_CSS_VAR_KEYS = [
  '--rune-letter-tint-player-a',
  '--rune-letter-tint-player-b',
  '--rune-tier-forged-ring-color',
  '--rune-tier-rare-inlay-color',
  '--rune-tier-mythic-glow-color',
  '--rune-tier-mythic-sparkle-color',
  '--rune-anchor-sigil-color-player-a',
  '--rune-anchor-sigil-color-player-b',
  '--rune-hollow-crack-color',
  '--rune-affinity-halo-color',
  '--rune-affinity-halo-opacity',
] as const

export type RuneCssVarKey = (typeof RUNE_CSS_VAR_KEYS)[number]

/** The scalar (non-hex) Rune tokens; excluded from pixi_uniforms. */
export const RUNE_SCALAR_CSS_VAR_KEYS = [
  '--rune-affinity-halo-opacity',
] as const

/**
 * snake_case mirror of the css-var keys for Pixi shader uniform binding.
 * `--color-bg-deep` → `color_bg_deep`. Computed once at module load.
 */
export const PIXI_UNIFORM_KEYS = CSS_VAR_KEYS.map((k) =>
  k.replace(/^--/, '').replace(/-/g, '_'),
) as ReadonlyArray<string>

/**
 * snake_case Rune pixi uniform keys (10 of 11 — opacity scalar excluded).
 */
export const RUNE_PIXI_UNIFORM_KEYS = RUNE_CSS_VAR_KEYS.filter(
  (k) => !(RUNE_SCALAR_CSS_VAR_KEYS as readonly string[]).includes(k),
).map((k) => k.replace(/^--/, '').replace(/-/g, '_')) as ReadonlyArray<string>

export interface ThemeDescriptor {
  schema_version: 1
  theme_key: ThemeKey
  display_name: { en: string; sv: string }
  /**
   * Plan 30-01 — widened from `Record<CssVarKey, string>` to allow Rune
   * keys (`--rune-*`) alongside the 13 theme-identity keys. Validator
   * still enforces all 13 CssVarKey + all 11 RuneCssVarKey are present.
   */
  css_vars: Record<CssVarKey, string> & Partial<Record<RuneCssVarKey, string>>
  /**
   * Snake-cased mirror of `css_vars` keys; values are RGB triples in [0..1].
   * Pixi materials read from this map at apply-time.
   */
  pixi_uniforms: Record<string, [number, number, number]>
  assets: {
    sprite_atlas: string
    sprite_image: string
    audio_pack: {
      letter_trace: string
      token_clink: string
      queen_organ: string
      cube_thock: string
      ribbon_burst: string
      ambient_loop: string
    }
  }
  colorblind_certified: {
    deuteranopia_delta_e: number
    protanopia_delta_e: number
    tritanopia_delta_e: number
    minimum_required: 30
    certified_at: string
  }
}

/** Runtime guard: is this string a valid ThemeKey? */
export function isThemeKey(s: string): s is ThemeKey {
  return (THEME_KEYS as ReadonlyArray<string>).includes(s)
}

/**
 * Runtime guard for ThemeDescriptor shape. Used by load.ts before applying
 * an alternate-theme manifest pulled from R2 (T-08-THEME-INJ mitigation —
 * never apply an unverified descriptor).
 */
export function isValidThemeDescriptor(d: unknown): d is ThemeDescriptor {
  if (typeof d !== 'object' || d === null) return false
  const o = d as Record<string, unknown>
  if (o.schema_version !== 1) return false
  if (typeof o.theme_key !== 'string' || !isThemeKey(o.theme_key)) return false
  if (typeof o.display_name !== 'object' || o.display_name === null)
    return false
  const dn = o.display_name as Record<string, unknown>
  if (typeof dn.en !== 'string' || typeof dn.sv !== 'string') return false
  if (typeof o.css_vars !== 'object' || o.css_vars === null) return false
  const cv = o.css_vars as Record<string, unknown>
  for (const key of CSS_VAR_KEYS) {
    if (typeof cv[key] !== 'string') return false
    if (!/^#[0-9A-Fa-f]{6}$/.test(cv[key] as string)) return false
  }
  // Plan 30-01 — require all 11 --rune-* tokens. 10 hex colors + 1 scalar
  // opacity ('--rune-affinity-halo-opacity' which carries a "0.18" float
  // string). T-30-04 mitigation: any missing token would cause
  // LetterCubeSprite to read `undefined` and paint black/transparent.
  for (const key of RUNE_CSS_VAR_KEYS) {
    if (typeof cv[key] !== 'string') return false
    if ((RUNE_SCALAR_CSS_VAR_KEYS as readonly string[]).includes(key)) {
      const v = Number(cv[key] as string)
      if (!Number.isFinite(v) || v < 0 || v > 1) return false
    } else if (!/^#[0-9A-Fa-f]{6}$/.test(cv[key] as string)) {
      return false
    }
  }
  // Allow only known keys (13 theme-identity + 11 rune).
  const ALLOWED_CSS_KEYS = new Set<string>([
    ...(CSS_VAR_KEYS as ReadonlyArray<string>),
    ...(RUNE_CSS_VAR_KEYS as ReadonlyArray<string>),
  ])
  for (const key of Object.keys(cv)) {
    if (!ALLOWED_CSS_KEYS.has(key)) return false
  }
  if (typeof o.pixi_uniforms !== 'object' || o.pixi_uniforms === null)
    return false
  const pu = o.pixi_uniforms as Record<string, unknown>
  for (const ukey of PIXI_UNIFORM_KEYS) {
    const triple = pu[ukey]
    if (!Array.isArray(triple) || triple.length !== 3) return false
    for (const ch of triple) {
      if (typeof ch !== 'number' || ch < 0 || ch > 1) return false
    }
  }
  // Plan 30-01 — require all 10 rune color tokens mirrored in pixi_uniforms.
  for (const ukey of RUNE_PIXI_UNIFORM_KEYS) {
    const triple = pu[ukey]
    if (!Array.isArray(triple) || triple.length !== 3) return false
    for (const ch of triple) {
      if (typeof ch !== 'number' || ch < 0 || ch > 1) return false
    }
  }
  if (typeof o.assets !== 'object' || o.assets === null) return false
  if (
    typeof o.colorblind_certified !== 'object' ||
    o.colorblind_certified === null
  )
    return false
  return true
}

/** ThemeManifestInvalidError — load.ts throws this on validation failure. */
export class ThemeManifestInvalidError extends Error {
  constructor(themeKey: string, reason: string) {
    super(`ThemeManifestInvalidError: ${themeKey} — ${reason}`)
    this.name = 'ThemeManifestInvalidError'
  }
}
